import { FullFormState } from "../Store/FullFormState";
import { MultiLineSort } from "../../../Controls/MultiControls/MultiLine/MultiLineTypes";
import { currentViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { filterInstancesBatchedAsync } from "../../../Helpers/FilteringHelpers";
import { sortInstances } from "../../../Helpers/SortingHelpers";
import { FilterExpression } from "../../../Helpers/FilterExpressions/FilterExpression";
import {
    DataFieldRow,
    DataFieldValue,
} from "../../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryResponse";
import { getIn } from "../../../Helpers/ReduxStateHelpers";
import { NotImplementedError } from "../../../../../Common/Errors";
import { RelativeModelFieldPath } from "../../../../../Common/ModelPath/RelativeModelFieldPath";

import { FilterResult, IFilteringStrategy } from "./IFilteringStrategy";
import { AggregationType } from "./IAggregationQueryStrategy";

export class ClientSideFilteringStrategy implements IFilteringStrategy {
    private readonly getState: () => FullFormState;

    public constructor(getState: () => FullFormState) {
        this.getState = getState;
    }

    public async performFilteringAndSorting(
        multilinePath: string,
        filters: FilterExpression[],
        multilineSort?: MultiLineSort,
        abortSignal?: AbortSignal
    ): Promise<FilterResult> {
        const state = this.getState();
        const multilineState = currentViewModelSelect.node(state, multilinePath);
        const filteredInstances = await filterInstancesBatchedAsync(
            state,
            currentViewModelSelect,
            filters,
            multilinePath,
            100,
            abortSignal
        );
        const sortedInstances = sortInstances(filteredInstances, multilineState, multilineSort);
        return sortedInstances;
    }

    public async performFilteringAndSortingItems(
        multilinePath: string,
        requestPaths: RelativeModelFieldPath[],
        filters: FilterExpression[],
        multilineSort?: MultiLineSort,
        abortSignal?: AbortSignal
    ): Promise<DataFieldRow[]> {
        const state = this.getState();
        const multilineState = currentViewModelSelect.node(state, multilinePath);
        const sortedInstances = await this.performFilteringAndSorting(
            multilinePath,
            filters,
            multilineSort,
            abortSignal
        );

        const result = sortedInstances.map(instance => [
            instance,
            ...requestPaths.map(requestPath => {
                const relativeModelFieldPath = RelativeModelFieldPath.createFromString(`${instance}/${requestPath}`);
                const requestFieldPath = [
                    ...relativeModelFieldPath.path.getSimpleTokensAsArrayStrict(),
                    ".",
                    relativeModelFieldPath.field,
                ];
                return getIn(multilineState, requestFieldPath, "") as DataFieldValue;
            }),
        ]);

        return result;
    }

    public getAggregationValue(
        multilinePath: string,
        pathToValue: RelativeModelFieldPath,
        aggregationType: AggregationType
    ): Promise<DataFieldValue> {
        const state = this.getState();
        const multilineState = currentViewModelSelect.node(state, multilinePath);
        const instances = currentViewModelSelect.children(state, multilinePath);
        const values = instances.map(instance => {
            const relativeModelFieldPath = RelativeModelFieldPath.createFromString(`${instance}/${pathToValue}`);
            const requestFieldPath = [
                ...relativeModelFieldPath.path.getSimpleTokensAsArrayStrict(),
                ".",
                relativeModelFieldPath.field,
            ];
            return getIn(multilineState, requestFieldPath, "") as DataFieldValue;
        });

        switch (aggregationType) {
            case AggregationType.Max: {
                const result = values.reduce((acc, it) => (it == undefined ? acc : Math.max(acc, Number(it))), 0);
                return Promise.resolve(String(result));
            }
            default:
                throw new NotImplementedError();
        }
    }
}
